import React, { useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { UI, cardSx, dateShort, tableSx, value } from './operationalUi';
import type { MetricDefinition, OperationalRow } from './types';

interface Props {
  row: OperationalRow;
  definitions: MetricDefinition[];
  onCampaign: (id: string) => void;
  onExport: () => void;
}
export default function CampaignEvidence({ row, definitions, onCampaign, onExport }: Props) {
  const [mode, setMode] = useState(''),
    peers = row.peerCampaigns || [],
    peerCount = peers.length;
  const openCampaign = (id: string) => {
    setMode('');
    onCampaign(id);
  };
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
        sx={{ ...cardSx, p: 1.5, flexShrink: 0 }}
      >
        <Typography sx={{ fontSize: 13 }}>
          <Box component="span" sx={{ fontWeight: 600, color: UI.teal, mr: 1 }}>
            EVIDENCE
          </Box>
          {peerCount} eligible completed {row.level === 'country' ? row.country : row.destination} {row.type} peers of
          28 to 42 days{peerCount !== row.n ? `; ${row.n} with margin/day records` : ''}.
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button size="small" onClick={() => setMode('peers')}>
            Show the {peerCount} peer campaigns
          </Button>
          <Button size="small" onClick={() => setMode('spread')}>
            Spread across seven measures
          </Button>
          <Button size="small" onClick={onExport}>
            Export
          </Button>
        </Stack>
      </Stack>
      <Dialog open={!!mode} onClose={() => setMode('')} fullWidth maxWidth="lg">
        <DialogTitle>{mode === 'peers' ? 'Peer campaigns' : 'Spread across seven measures'}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: 13, color: UI.muted, mb: 1 }}>
            {peerCount === row.n
              ? `${peerCount} eligible campaigns, all with valid margin/day records.`
              : `${peerCount} eligible campaigns; ${row.n} with valid margin/day records.`}{' '}
            Each measure uses its own valid records.
          </Typography>
          <TableContainer>
            <Table size="small" sx={tableSx}>
              <TableHead>
                <TableRow>
                  {(mode === 'peers'
                    ? ['Campaign', 'Dates', 'Days', 'Packages/day', 'Margin/day']
                    : ['Measure', 'Valid peers', 'Q1', 'Median', 'Q3']
                  ).map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {mode === 'peers'
                  ? peers.map((peer) => (
                      <TableRow
                        hover
                        key={peer.id}
                        sx={{ cursor: 'pointer' }}
                        tabIndex={0}
                        onClick={() => openCampaign(peer.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openCampaign(peer.id);
                          }
                        }}
                      >
                        <TableCell>{peer.name}</TableCell>
                        <TableCell>
                          {dateShort(peer.startDate)}–{dateShort(peer.endDate)}
                        </TableCell>
                        <TableCell>{peer.durationDays}</TableCell>
                        <TableCell>{value(peer.metrics.packages_per_day)}</TableCell>
                        <TableCell>{value(peer.metrics.margin_per_day, 'currency')}</TableCell>
                      </TableRow>
                    ))
                  : definitions.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{d.label}</TableCell>
                        <TableCell>{row.metrics[d.id]?.n || 0}</TableCell>
                        <TableCell>{value(row.metrics[d.id]?.q1, d.unit)}</TableCell>
                        <TableCell>{value(row.metrics[d.id]?.median, d.unit)}</TableCell>
                        <TableCell>{value(row.metrics[d.id]?.q3, d.unit)}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMode('')}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
